/**
 * Visibility Store — Phase 4, Module 4 (AD-013).
 *
 * Reads and writes the global visibility matrix from `settings/portalVisibility`.
 * Per-project overrides are stored on `projects/{pid}.visibilityOverrides` —
 * see `projectStore.ts` extensions for the per-project functions.
 *
 * Impure — imports Firebase. Keep pure logic in `visibilityModel.ts`.
 */
import {
  doc,
  getDoc,
  onSnapshot,
  setDoc,
  updateDoc,
  type Unsubscribe,
} from 'firebase/firestore';
import { db } from './firebase';
import {
  APPROVED_DEFAULTS,
  PORTAL_ROLES,
  PORTAL_SECTIONS,
  type PortalRole,
  type PortalSection,
  type VisibilityMatrix,
} from './visibilityModel';

// ─── Read ─────────────────────────────────────────────────────────────────

/**
 * One-time read of the global visibility matrix.
 * Falls back to `APPROVED_DEFAULTS` when the doc is missing or empty.
 */
export async function getVisibilityMatrix(): Promise<VisibilityMatrix> {
  const snap = await getDoc(doc(db, 'settings', 'portalVisibility'));
  if (!snap.exists()) return { ...APPROVED_DEFAULTS };
  return (snap.data() as VisibilityMatrix) ?? { ...APPROVED_DEFAULTS };
}

/**
 * Live listener for the global visibility matrix.
 * Calls `cb(matrix)` immediately with the current value and on every change.
 * Falls back to `APPROVED_DEFAULTS` when the doc is missing.
 * Returns the Unsubscribe function.
 */
export function listenVisibilityMatrix(
  cb: (matrix: VisibilityMatrix) => void,
): Unsubscribe {
  return onSnapshot(doc(db, 'settings', 'portalVisibility'), (snap) => {
    if (!snap.exists()) {
      cb({ ...APPROVED_DEFAULTS });
      return;
    }
    cb((snap.data() as VisibilityMatrix) ?? { ...APPROVED_DEFAULTS });
  });
}

// ─── Write ────────────────────────────────────────────────────────────────

/**
 * Save a complete new visibility matrix. Overwrites `settings/portalVisibility`.
 * Admin-only (enforced by Firestore rules `allow write: if isAdmin()`).
 *
 * @param matrix   — the full matrix to persist
 * @param actorUid — for audit (stored as `updatedBy`)
 */
export async function saveVisibilityMatrix(
  matrix: VisibilityMatrix,
  actorUid: string,
): Promise<void> {
  await setDoc(doc(db, 'settings', 'portalVisibility'), {
    ...matrix,
    _updatedBy: actorUid,
    _updatedAt: new Date().toISOString(),
  });
}

// ─── Cell-level toggle ────────────────────────────────────────────────────

/**
 * Toggle a single cell in the global visibility matrix. Uses a dot-notation
 * updateDoc so the surrounding cells are untouched.
 *
 * Guards:
 *   - `role === 'admin'` → throws (admin row is always all-true, non-negotiable).
 *   - `section === 'internal_chat'` for any non-staff role → throws
 *     (internal chat is hard-gated in `canSee` regardless of the matrix value;
 *     persisting a different value would mislead the Settings UI).
 *
 * @param role    — the role row to update
 * @param section — the section column to update
 * @param value   — the new boolean value
 * @param actorUid — for audit
 */
export async function toggleGlobalCell(
  role: PortalRole,
  section: PortalSection,
  value: boolean,
  actorUid: string,
): Promise<void> {
  if (role === 'admin') {
    throw new Error('toggleGlobalCell: the admin row is always all-true and cannot be modified.');
  }
  if (section === 'internal_chat' && role !== 'staff') {
    throw new Error(
      `toggleGlobalCell: internal_chat cannot be enabled for role "${role}" — it is hard-gated in canSee.`,
    );
  }
  await updateDoc(doc(db, 'settings', 'portalVisibility'), {
    [`${role}.${section}`]: value,
    _updatedBy: actorUid,
    _updatedAt: new Date().toISOString(),
  });
}

// ─── Validate all roles / sections are represented ────────────────────────

/**
 * PURE — check a candidate matrix has a value for every role × section pair.
 * Returns an array of missing `role.section` strings; empty = valid.
 */
export function missingMatrixCells(matrix: Partial<VisibilityMatrix>): string[] {
  const missing: string[] = [];
  for (const role of PORTAL_ROLES) {
    for (const section of PORTAL_SECTIONS) {
      if (typeof matrix?.[role]?.[section] !== 'boolean') {
        missing.push(`${role}.${section}`);
      }
    }
  }
  return missing;
}
